import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// This component uses Google Identity Services (GSI). The Client ID comes from
// public site settings, which the admin controls in /admin/settings. If Google
// Sign-In is disabled or no Client ID is set, this component renders nothing.

let gsiScriptPromise = null;
function loadGsiScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  if (window.google?.accounts?.id) return Promise.resolve(window.google);
  if (!gsiScriptPromise) {
    gsiScriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://accounts.google.com/gsi/client";
      s.async = true;
      s.defer = true;
      s.onload = () => resolve(window.google);
      s.onerror = () => reject(new Error("Failed to load Google Identity Services"));
      document.head.appendChild(s);
    });
  }
  return gsiScriptPromise;
}

export default function GoogleSignInButton({ clientId, mode = "signin_with", onSuccess }) {
  const btnRef = useRef(null);
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    loadGsiScript()
      .then((google) => {
        if (cancelled || !btnRef.current) return;
        google.accounts.id.initialize({
          client_id: clientId,
          callback: async (resp) => {
            const idToken = resp?.credential;
            if (!idToken) { toast.error("Google sign-in cancelled"); return; }
            try {
              const { data } = await api.post("/auth/google", { credential: idToken });
              await refresh();
              if (onSuccess) onSuccess(data);
              else nav(data.user?.role === "admin" ? "/admin" : "/login-deal");
              toast.success(data.new_account ? "Welcome to Start-A-TE!" : `Welcome back, ${data.user?.username}!`);
            } catch (err) { toast.error(formatApiError(err)); }
          },
          auto_select: false,
          ux_mode: "popup",
        });
        google.accounts.id.renderButton(btnRef.current, {
          theme: "outline",
          size: "large",
          text: mode,
          shape: "pill",
          width: 320,
        });
        setReady(true);
      })
      .catch(() => setReady(false));
    return () => { cancelled = true; };
  }, [clientId, mode, refresh, nav, onSuccess]);

  if (!clientId) return null;
  return (
    <div className="flex flex-col items-center gap-2" data-testid="google-signin-wrapper">
      <div ref={btnRef} data-testid="google-signin-btn" />
      {!ready && <div className="text-xs text-slate-500">Loading Google…</div>}
    </div>
  );
}
